'use client'

import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { usePathname, useSearchParams } from 'next/navigation'
import { useAuth } from '@/lib/auth/AuthProvider'
import { fetchPlayerContext } from '@/lib/player/playerContextClient'
import type { LeagueCode } from '@/lib/create/league-codes'

// Types for instance management
export interface UserInstance {
  id: string
  user_id: string
  play_mode: 'cash' | 'token' | null
  team_name: string | null
  nt_token_balance: number
  creation_completed: boolean
  creation_step: string
  instance_status: string
  season_id: string
  created_at: string
  updated_at?: string
  league_id: string | null
  league_participant_id: string | null
  selected_leagues?: LeagueCode[]
  league_count?: number
  lobby_assignment_status?: string | null
  squad_size?: number
  formation_name?: string | null
  has_captain?: boolean
  is_complete?: boolean
}

export interface InstanceContextType {
  // Current state
  currentInstanceId: string | null
  currentInstance: UserInstance | null
  allInstances: UserInstance[]

  // Actions
  setCurrentInstanceId: (instanceId: string) => void
  refreshInstances: () => Promise<void>

  // Status
  isLoading: boolean
  error: string | null
}

// Create context
const InstanceContext = createContext<InstanceContextType | null>(null)

// Instance provider props
interface InstanceProviderProps {
  children: ReactNode
}

export function InstanceProvider({ children }: InstanceProviderProps) {
  const { user } = useAuth()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [currentInstanceId, setCurrentInstanceIdState] = useState<string | null>(null)
  const routeScopedInstanceId =
    pathname.startsWith('/create') || pathname === '/dashboard'
      ? (searchParams.get('instance') ?? null)
      : null

  // Fetch all user instances
  const {
    data: allInstances = [],
    isLoading,
    error,
    refetch: refreshInstances
  } = useQuery({
    queryKey: ['userInstances', user?.id],
    queryFn: async (): Promise<UserInstance[]> => {
      if (!user?.id) return []
      const context = await fetchPlayerContext()
      return context.instances as UserInstance[]
    },
    enabled: !!user?.id,
    staleTime: 2 * 60 * 1000 // 2 minutes
  })

  // Get current instance object
  const resolvedCurrentInstanceId = routeScopedInstanceId || currentInstanceId
  const currentInstance = resolvedCurrentInstanceId
    ? allInstances.find((instance) => instance.id === resolvedCurrentInstanceId) || null
    : null

  // Auto-select first instance if none selected (only completed instances)
  useEffect(() => {
    if (resolvedCurrentInstanceId || allInstances.length === 0) return

    const timeoutId = setTimeout(() => {
      const savedInstanceId = localStorage.getItem('selectedInstanceId')
      const completedInstances = allInstances.filter((inst) => inst.is_complete === true)

      if (completedInstances.length === 0) {
        // No completed instances - user should be redirected to /drafts by middleware.
        console.warn(
          'InstanceProvider: No completed instances found. User should complete drafts first.'
        )
        return
      }

      const validSavedInstance = savedInstanceId
        ? completedInstances.find((instance) => instance.id === savedInstanceId)
        : null

      setCurrentInstanceIdState(validSavedInstance ? savedInstanceId : completedInstances[0].id)
    }, 0)

    return () => clearTimeout(timeoutId)
  }, [allInstances, resolvedCurrentInstanceId])

  useEffect(() => {
    if (!user?.id || isLoading) return

    const savedInstanceId = localStorage.getItem('selectedInstanceId')
    if (savedInstanceId && !allInstances.some((instance) => instance.id === savedInstanceId)) {
      localStorage.removeItem('selectedInstanceId')
    }

    if (currentInstanceId && !allInstances.some((instance) => instance.id === currentInstanceId)) {
      setCurrentInstanceIdState(null)
    }
  }, [allInstances, currentInstanceId, isLoading, user?.id])

  // Set current instance with persistence
  const setCurrentInstanceId = (instanceId: string) => {
    setCurrentInstanceIdState(instanceId)
    localStorage.setItem('selectedInstanceId', instanceId)
  }

  const contextValue: InstanceContextType = {
    currentInstanceId: resolvedCurrentInstanceId,
    currentInstance,
    allInstances,
    setCurrentInstanceId,
    refreshInstances: async () => {
      await refreshInstances()
    },
    isLoading,
    error: error?.message || null
  }

  return <InstanceContext.Provider value={contextValue}>{children}</InstanceContext.Provider>
}

// Hook to use instance context
export function useInstanceContext() {
  const context = useContext(InstanceContext)
  if (!context) {
    throw new Error('useInstanceContext must be used within an InstanceProvider')
  }
  return context
}
